import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * OWL DREAM — POLICY PAGE (i18n)
 * - Responsive
 * - TOC + search
 * - Anchor jump + highlight theo hash
 * - Copy link + toast
 */

type PolicySection = {
  id: string;
  titleKey: string;       // i18n key
  searchTextKey: string;  // i18n key (plain text) để search
  content: React.ReactNode;
};

const safe = (v?: string) => (v && v.trim() ? v : "—");

function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={[
        "rounded-3xl border border-black/5 bg-white/70 backdrop-blur",
        "shadow-[0_10px_40px_-20px_rgba(0,0,0,0.35)]",
        className,
      ].join(" ")}
    >
      {children}
    </section>
  );
}

export default function PolicyPage() {
  const { t } = useTranslation();
  const { hash } = useLocation();

  const [q, setQ] = useState("");
  const [toast, setToast] = useState<string>("");

  // ===== sections (content vẫn JSX, text search dùng key plain) =====
  const sections: PolicySection[] = useMemo(
    () => [
      {
        id: "shipping",
        titleKey: "policy.sections.shipping.title",
        searchTextKey: "policy.sections.shipping.searchText",
        content: (
          <div className="space-y-3 text-sm text-neutral-700 leading-relaxed">
            <p>{t("policy.sections.shipping.p1")}</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>{t("policy.sections.shipping.li1")}</li>
              <li>{t("policy.sections.shipping.li2")}</li>
              <li>{t("policy.sections.shipping.li3")}</li>
            </ul>
            <p className="text-neutral-500">{t("policy.sections.shipping.note")}</p>
          </div>
        ),
      },
      {
        id: "payment",
        titleKey: "policy.sections.payment.title",
        searchTextKey: "policy.sections.payment.searchText",
        content: (
          <div className="space-y-3 text-sm text-neutral-700 leading-relaxed">
            <p>{t("policy.sections.payment.p1")}</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>{t("policy.sections.payment.li1")}</li>
              <li>{t("policy.sections.payment.li2")}</li>
              <li>{t("policy.sections.payment.li3")}</li>
            </ul>
            <p className="text-neutral-500">{t("policy.sections.payment.note")}</p>
          </div>
        ),
      },
      {
        id: "returns",
        titleKey: "policy.sections.returns.title",
        searchTextKey: "policy.sections.returns.searchText",
        content: (
          <div className="space-y-3 text-sm text-neutral-700 leading-relaxed">
            <p>{t("policy.sections.returns.p1")}</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>{t("policy.sections.returns.li1")}</li>
              <li>{t("policy.sections.returns.li2")}</li>
              <li>{t("policy.sections.returns.li3")}</li>
            </ul>
            <p className="text-neutral-500">{t("policy.sections.returns.note")}</p>
          </div>
        ),
      },
      {
        id: "warranty",
        titleKey: "policy.sections.warranty.title",
        searchTextKey: "policy.sections.warranty.searchText",
        content: (
          <div className="space-y-3 text-sm text-neutral-700 leading-relaxed">
            <p>{t("policy.sections.warranty.p1")}</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>{t("policy.sections.warranty.li1")}</li>
              <li>{t("policy.sections.warranty.li2")}</li>
            </ul>
          </div>
        ),
      },
      {
        id: "privacy",
        titleKey: "policy.sections.privacy.title",
        searchTextKey: "policy.sections.privacy.searchText",
        content: (
          <div className="space-y-3 text-sm text-neutral-700 leading-relaxed">
            <p>{t("policy.sections.privacy.p1")}</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>{t("policy.sections.privacy.li1")}</li>
              <li>{t("policy.sections.privacy.li2")}</li>
              <li>{t("policy.sections.privacy.li3")}</li>
            </ul>
          </div>
        ),
      },
      {
        id: "terms",
        titleKey: "policy.sections.terms.title",
        searchTextKey: "policy.sections.terms.searchText",
        content: (
          <div className="space-y-3 text-sm text-neutral-700 leading-relaxed">
            <p>{t("policy.sections.terms.p1")}</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>{t("policy.sections.terms.li1")}</li>
              <li>{t("policy.sections.terms.li2")}</li>
            </ul>
            <p className="text-neutral-500">{t("policy.sections.terms.note")}</p>
          </div>
        ),
      },
    ],
    [t],
  );

  const activeId = useMemo(() => {
    const h = (hash || "").replace("#", "").trim();
    return h || "shipping";
  }, [hash]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return sections;

    return sections.filter((s) => {
      const title = t(s.titleKey).toLowerCase();
      const searchText = t(s.searchTextKey).toLowerCase();
      const id = s.id.toLowerCase();

      return (
        title.includes(query) ||
        id.includes(query) ||
        searchText.includes(query)
      );
    });
  }, [q, sections, t]);

  const jump = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
  };

  // auto scroll khi có hash
  useEffect(() => {
    const id = activeId;
    const el = document.getElementById(id);
    if (!el) return;
    // delay nhẹ để layout ổn định
    const tmr = window.setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => window.clearTimeout(tmr);
  }, [activeId]);

  // toast auto hide
  useEffect(() => {
    if (!toast) return;
    const tmr = window.setTimeout(() => setToast(""), 1800);
    return () => window.clearTimeout(tmr);
  }, [toast]);

  const copyLink = async (id: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    try {
      await navigator.clipboard?.writeText(url);
      setToast(t("policy.toastCopied"));
    } catch {
      // fallback
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setToast(t("policy.toastCopied"));
      } catch {
        setToast(t("policy.toastCopyFail"));
      }
    }
  };

  return (
    <div className="min-h-[70vh] bg-[#f6f3ee]">
      {/* TOAST */}
      {toast ? (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
          <div className="rounded-2xl bg-black text-white text-xs px-4 py-2 shadow-lg">
            {toast}
          </div>
        </div>
      ) : null}

      {/* HEADER */}
      <div className="max-w-6xl mx-auto px-4 pt-14 pb-8">
        <p className="text-[11px] uppercase tracking-[0.45em] text-neutral-600">
          {t("policy.brandKicker")}
        </p>

        <div className="mt-3 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-neutral-900">
              {t("policy.title")}
            </h1>
            <p className="mt-2 text-sm text-neutral-600">
              {t("policy.subtitle")}
            </p>
          </div>

          <div className="flex gap-2">
            <Link
              to="/shop"
              className="rounded-2xl px-4 py-2.5 text-sm font-medium bg-black text-white hover:opacity-90 transition"
            >
              {t("policy.shop")}
            </Link>
            <Link
              to="/profile"
              className="rounded-2xl px-4 py-2.5 text-sm font-medium border border-black/10 bg-white hover:bg-black hover:text-white transition"
            >
              {t("policy.account")}
            </Link>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT: MENU */}
          <Card className="p-6 lg:sticky lg:top-6 h-fit">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-neutral-900">
                {t("policy.toc")}
              </h2>
              <span className="text-xs text-neutral-500">
                {filtered.length}/{sections.length}
              </span>
            </div>

            <div className="mt-4">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("policy.searchPlaceholder")}
                className="w-full rounded-2xl border border-black/10 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-black/10"
              />
              <p className="mt-2 text-xs text-neutral-500">
                {t("policy.searchHint")}
              </p>
            </div>

            <div className="mt-5 space-y-2">
              {filtered.map((s) => {
                const active = s.id === activeId;
                return (
                  <button
                    key={s.id}
                    onClick={() => jump(s.id)}
                    className={[
                      "w-full text-left rounded-2xl px-4 py-3 text-sm transition",
                      active
                        ? "bg-black text-white"
                        : "bg-white hover:bg-black hover:text-white border border-black/5",
                    ].join(" ")}
                  >
                    {t(s.titleKey)}
                  </button>
                );
              })}

              {filtered.length === 0 && (
                <div className="rounded-2xl border border-dashed border-black/15 bg-white p-5">
                  <p className="text-sm text-neutral-600">
                    {t("policy.noResult")}{" "}
                    <span className="font-medium">{safe(q)}</span>
                  </p>
                  <button
                    onClick={() => setQ("")}
                    className="mt-3 text-xs uppercase tracking-widest underline underline-offset-4"
                  >
                    {t("policy.clearSearch")}
                  </button>
                </div>
              )}
            </div>
          </Card>

          {/* RIGHT: SECTIONS */}
          <div className="lg:col-span-2 space-y-6">
            {sections.map((s) => {
              const active = s.id === activeId;
              return (
                <Card
                  key={s.id}
                  className={[
                    "p-6 transition",
                    active ? "ring-1 ring-black/10" : "",
                  ].join(" ")}
                >
                  {/* anchor */}
                  <div id={s.id} className="scroll-mt-24" />

                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-lg font-semibold text-neutral-900">
                      {t(s.titleKey)}
                    </h3>

                    <button
                      onClick={() => copyLink(s.id)}
                      className="text-xs uppercase tracking-widest underline underline-offset-4 text-neutral-600 hover:text-black"
                      title={t("policy.copyLink")}
                    >
                      {t("policy.copyLink")}
                    </button>
                  </div>

                  <div className="mt-4">{s.content}</div>
                </Card>
              );
            })}

            {/* SUPPORT */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-neutral-900">
                {t("policy.supportTitle")}
              </h3>
              <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
                {t("policy.supportDesc")}
              </p>

              <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                <div className="rounded-2xl bg-white border border-black/5 p-4">
                  <p className="text-xs uppercase tracking-[0.35em] text-neutral-500">
                    {t("policy.hotline")}
                  </p>
                  <p className="mt-2 font-semibold text-neutral-900">—</p>
                  <p className="mt-1 text-neutral-600">
                    {t("policy.workHours")}
                  </p>
                </div>

                <div className="rounded-2xl bg-white border border-black/5 p-4">
                  <p className="text-xs uppercase tracking-[0.35em] text-neutral-500">
                    {t("policy.email")}
                  </p>
                  <p className="mt-2 font-semibold text-neutral-900">—</p>
                  <p className="mt-1 text-neutral-600">{t("policy.replyIn")}</p>
                </div>
              </div>

              <div className="mt-4">
                <Link
                  to="/profile"
                  className="inline-flex rounded-2xl px-4 py-2.5 text-sm font-medium bg-black text-white hover:opacity-90 transition"
                >
                  {t("policy.viewOrders")}
                </Link>
              </div>
            </Card>

            <p className="text-xs text-neutral-500 px-2">
              {t("policy.lastUpdated")} <span className="font-medium">—</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
